import { Avatar, Chip, Paper, Typography } from '@/ui';

import { formatIsoTimeRange } from '../coachSchedule/calendarGrid';

import OpsSessionActionButtons from './OpsSessionActionButtons';
import {
  adminMemberRowActions,
  adminMemberRowEndAt,
  adminMemberRowIsCancelled,
  adminMemberRowStartAt,
} from './composeAdminMemberSessionRows';

import type { AdminMemberSessionRow } from './composeAdminMemberSessionRows';

export interface AdminMemberSessionCardProps {
  row: AdminMemberSessionRow;
  timeZone: string;
  now: number;
  // MEN-5338: Ops actions; the clicked button is passed for focus return.
  onCancel?: (target: HTMLElement) => void;
  onReschedule?: (target: HTMLElement) => void;
  actionPending?: boolean;
}

const AdminMemberSessionCard = ({
  row,
  timeZone,
  now,
  onCancel,
  onReschedule,
  actionPending = false,
}: AdminMemberSessionCardProps) => {
  const start = adminMemberRowStartAt(row);
  const end = adminMemberRowEndAt(row);
  const past = Date.parse(end) < now;
  const member =
    row.source === 'native'
      ? row.appointment.expectedIdentities.member.displayName
      : row.session.user.fullName;
  const coach =
    row.source === 'native'
      ? row.appointment.expectedIdentities.coach.displayName
      : row.session.coach.fullName;
  const avatar =
    row.source === 'native'
      ? row.appointment.expectedIdentities.member.avatarUrl
      : row.session.user.avatarUrl;
  let kind = row.source === 'legacy' ? row.session.title : 'Coaching';
  if (row.source === 'native' && row.appointment.kind === 'chemistry')
    kind = 'Chemistry';
  const seat = row.source === 'native' ? row.appointment.seatType : null;
  const rails = row.source === 'native' ? row.railsSession : row.session;
  const company = rails?.user.company?.name;
  const cancelled = adminMemberRowIsCancelled(row);
  // MEN-5355: cancelled wins over the past fill so it scans from the left.
  let fill = past ? 'bg-cement-100' : 'bg-white';
  if (cancelled) fill = 'bg-bloom-200/50';
  let label = past ? 'Past' : 'Scheduled';
  if (row.source === 'native') {
    const { appointment } = row;
    if (appointment.lifecycleStatus === 'reschedule_pending')
      label = 'Reschedule pending';
    if (appointment.lifecycleStatus === 'cancellation_pending')
      label = 'Cancellation pending';
    if (appointment.bookingStatus === 'pending') label = 'Booking pending';
    if (appointment.bookingStatus === 'rejected') label = 'Booking rejected';
    if (cancelled) {
      const { cancellation } = appointment;
      const initiator =
        cancellation?.initiator ??
        (cancellation?.actor.kind === 'person'
          ? cancellation.actor.role
          : null);
      label = initiator
        ? `Cancelled by ${initiator.charAt(0).toUpperCase()}${initiator.slice(1)}`
        : 'Cancelled';
    }
  } else {
    if (past && row.session.status === 'done') label = 'Attended';
    if (row.session.status === 'pending') label = 'Booking pending';
    if (cancelled) {
      const actor = row.session.cancelledBy;
      label = 'Cancelled';
      if (actor === 'coach') label = 'Cancelled by Coach';
      if (actor === 'member') label = 'Cancelled by Member';
    }
  }
  const allowed = adminMemberRowActions(row, now);
  const handleCancel = allowed.cancel ? onCancel : undefined;
  const handleReschedule = allowed.reschedule ? onReschedule : undefined;
  const actionable = Boolean(handleCancel || handleReschedule);
  const actionContext = `session on ${new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'long',
    timeZone,
    weekday: 'long',
  }).format(new Date(start))}`;
  return (
    <Paper
      className={`flex w-full min-w-0 flex-col gap-4 rounded-xl border border-cement-200 p-4 sm:flex-row sm:items-center sm:px-6 ${fill}`}
      elevation={1}
    >
      <Avatar
        firstName={member.split(' ')[0]}
        lastName={member.split(' ').slice(1).join(' ')}
        src={avatar ?? undefined}
      />
      <div className="min-w-0 flex-1">
        <Typography
          className={cancelled ? 'line-through' : undefined}
          variant="subtitle1"
        >
          {member}
        </Typography>
        {company && (
          <Typography
            className={cancelled ? 'line-through' : undefined}
            color="textSecondary"
            variant="caption"
          >
            {company}
          </Typography>
        )}
        <Typography
          color="textSecondary"
          variant="body2"
        >{`Coach: ${coach}`}</Typography>
        <Typography
          className={cancelled ? 'line-through' : undefined}
          color="textSecondary"
          variant="body2"
        >{`${formatIsoTimeRange(start, end, timeZone)} · ${kind}${seat ? ` · ${seat === 'executive' ? 'Executive' : 'Leadership'}` : ''} · ${(Date.parse(end) - Date.parse(start)) / 60000} min`}</Typography>
      </div>
      <Chip color={cancelled ? 'info' : 'success'} label={label} size="small" />
      {actionable && (
        <OpsSessionActionButtons
          context={actionContext}
          pending={actionPending}
          onCancel={handleCancel}
          onReschedule={handleReschedule}
        />
      )}
    </Paper>
  );
};

export default AdminMemberSessionCard;
